import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Grow,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { setIsCheckCOPQ } from '../../../../../Helpers/dashboardSlice';
import { MONTHLY_CHART_COLORS } from '../../../../../configs/colors';
import { NoRecords } from '../../../../../Helpers/Common';


const MonthlyChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length && payload[0].payload.count > 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '4px 8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #e5e7eb',
        borderRadius: '4px'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 400 }}>{`${payload[0].payload.fullMonth}`}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 400, display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>Click to view</Typography>
      </div>
    );
  }
  return null;
};

const CustomDot = (props) => {
  const { cx, cy, payload } = props;
  const offsetY = payload.count === 0 ? cy : cy - 5;
  return (
    <circle
      cx={cx}
      cy={offsetY}
      r={2}
      fill="#374151"
      stroke="none"
    />
  );
};

const CustomLineSegment = (props) => {
  const { points } = props;
  if (!points || points.length === 0) return null;
  let pathData = '';
  points.forEach((point, index) => {
    const offsetY = point.payload.count === 0 ? point.y : point.y - 5;
    if (index === 0) {
      pathData += `M ${point.x} ${offsetY}`;
    } else {
      pathData += ` L ${point.x} ${offsetY}`;
    }
  });
  if (!pathData) return null;
  return (
    <path
      d={pathData}
      stroke="#374151"
      strokeWidth={1}
      fill="none"
    />
  );
};

export default function NCTrendChart({ monthlyNCRData, monthlyCOPQData, handleMonthlyClick }) {
  const dispatch = useDispatch();
  const isCheckCOPQ = useSelector(state => state.dashboard.isCheckCOPQ);
  const chartData = (isCheckCOPQ ? monthlyCOPQData : monthlyNCRData)?.some(item => item.count > 0);

  const renderCustomizedLabel = (props) => {
    const { x, y, width, value, index } = props;
    if (value === 0) return null;
    const currentData = isCheckCOPQ ? monthlyCOPQData : monthlyNCRData;
    // Use index to get the correct data entry
    const dataEntry = currentData?.[index];
    
    return (
      <text x={x + width / 2 + 3} y={y - 10} fill="#000" textAnchor="middle" fontSize={13} fontWeight={400} style={{ cursor: 'pointer' }} onClick={() => dataEntry && handleMonthlyClick(dataEntry)}>
        <tspan>{value}</tspan>
      </text>
    );
  };

  return (
    <Grow in={true} timeout={1000}>
      <Grid item xs={12} md={12} lg={6} xl={4}>
        <Grow
          in={true}
          style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
          timeout={2100}
        >
          <Card sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 380, display: 'flex', flexDirection: 'column', borderRadius: '2%' }}>
            <CardContent sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', mt: 0 }}>
                  NC Volatility
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={isCheckCOPQ} size="small" onChange={() => dispatch(setIsCheckCOPQ(!isCheckCOPQ))} />}
                  label={<Typography variant="body2">COPQ</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
              {chartData ? (
                <Box sx={{ position: 'relative', width: '100%', height: '90%', display: 'flex', marginTop: '-20px' }}>
                  <Box sx={{ width: '10%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                    <svg width="30" height="300" style={{ display: 'flex', alignItems: 'center' }}>
                      <text
                        x="15"
                        y="150"
                        textAnchor="middle"
                        fill="#374151"
                        fontSize="11"
                        transform="rotate(-90 15 150)"
                        fontWeight="normal"
                      >
                        {isCheckCOPQ ? 'Value of NC (COPQ)' : 'Count of NC'}
                      </text>
                    </svg>
                  </Box>
                  <ResponsiveContainer width="90%" height="100%">
                    <ComposedChart data={isCheckCOPQ ? monthlyCOPQData : monthlyNCRData} margin={{ top: 40, right: 10, left: 0, bottom: 5 }} barCategoryGap="15%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                      />
                    <Tooltip content={<MonthlyChartTooltip />} cursor={false} />
                    <Bar
                      dataKey="count"
                      radius={[4, 4, 0, 0]}
                      label={renderCustomizedLabel}
                      isAnimationActive={false}
                      barSize={(isCheckCOPQ ? monthlyCOPQData : monthlyNCRData)?.length - 30 || 10}
                      onClick={(data) => handleMonthlyClick(data)}
                      style={{ cursor: 'pointer' }}
                    >
                      {monthlyNCRData?.map((entry, index) => {
                        return <Cell key={`cell-${index}`} fill={MONTHLY_CHART_COLORS[index % MONTHLY_CHART_COLORS.length]} />;
                      })}
                    </Bar>
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#374151"
                      strokeWidth={1}
                      dot={<CustomDot />}
                      isAnimationActive={true}
                      connectNulls={false}
                      shape={<CustomLineSegment />}
                    />
                  </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <NoRecords />
              )}
            </CardContent>
          </Card>
        </Grow>
      </Grid>
    </Grow>
  );
}
